import React, { useState } from 'react';
import {
  VStack, SimpleGrid, FormControl, FormLabel, Input, Select, Textarea,
  Button, HStack, Checkbox, useToast,
} from '@chakra-ui/react';
import { ImmTaller, crearTaller, actualizarTaller } from '../../../api/immApi';
import {
  SectionTitle, Field, IMM_COLOR, IMM_COLOR_HOVER, fmt,
} from '../_shared';

type Modo = 'new' | 'edit' | 'view';

interface Props {
  modo: Modo;
  data?: ImmTaller;
  onGuardado?: (t: ImmTaller) => void;
  onCancelar?: () => void;
}

const TIPOS = ['Taller', 'Curso', 'Conferencia', 'Platica', 'Actividad', 'Caminata', 'Evento'];
const ESTATUS_OPCIONES = ['Programado', 'Activo', 'Finalizado', 'Cancelado'];
const hoy = () => new Date().toISOString().split('T')[0];

const FormularioTaller: React.FC<Props> = ({ modo, data, onGuardado, onCancelar }) => {
  const toast = useToast();
  const [guardando, setGuardando] = useState(false);

  const [f, setF] = useState<Partial<ImmTaller>>({
    nombre: data?.nombre || '',
    tipo: data?.tipo || 'Taller',
    fecha: data?.fecha ? data.fecha.split('T')[0] : hoy(),
    horario: data?.horario || '',
    sede: data?.sede || '',
    instructor: data?.instructor || '',
    cupo: data?.cupo ?? undefined,
    descripcion: data?.descripcion || '',
    observaciones: data?.observaciones || '',
    publicadoPortal: data?.publicadoPortal ?? false,
    estatus: data?.estatus || 'Programado',
  });

  const set = (campo: keyof ImmTaller, valor: any) => setF((prev) => ({ ...prev, [campo]: valor }));

  const handleGuardar = async () => {
    if (!f.nombre?.trim()) {
      toast({ title: 'El nombre es obligatorio', status: 'warning', duration: 3000 });
      return;
    }
    setGuardando(true);
    try {
      const payload: Partial<ImmTaller> = {
        ...f,
        cupo: f.cupo != null && String(f.cupo) !== '' ? Number(f.cupo) : undefined,
      };
      const res = modo === 'edit' && data
        ? await actualizarTaller(data.id, payload)
        : await crearTaller(payload);
      toast({ title: modo === 'edit' ? 'Taller actualizado' : 'Taller creado', status: 'success', duration: 3000 });
      onGuardado?.(res);
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message || 'No se pudo guardar el taller.', status: 'error', duration: 4000 });
    } finally {
      setGuardando(false);
    }
  };

  if (modo === 'view' && data) {
    return (
      <VStack spacing={3} align="stretch">
        <SectionTitle>Datos del taller</SectionTitle>
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
          <Field label="Nombre" value={data.nombre} />
          <Field label="Tipo" value={data.tipo} />
          <Field label="Fecha" value={fmt(data.fecha)} />
          <Field label="Horario" value={data.horario} />
          <Field label="Sede" value={data.sede} />
          <Field label="Instructor" value={data.instructor} />
          <Field label="Cupo" value={data.cupo != null ? String(data.cupo) : '—'} />
          <Field label="Publicado al portal" value={data.publicadoPortal ? 'Sí' : 'No'} />
          <Field label="Estatus" value={data.estatus} />
        </SimpleGrid>
        <SectionTitle>Detalle</SectionTitle>
        <SimpleGrid columns={{ base: 1 }} spacing={4}>
          <Field label="Descripción" value={data.descripcion} />
          <Field label="Observaciones" value={data.observaciones} />
        </SimpleGrid>
        <HStack justify="flex-end" pt={2}>
          <Button variant="outline" onClick={onCancelar}>Cerrar</Button>
        </HStack>
      </VStack>
    );
  }

  return (
    <VStack spacing={3} align="stretch">
      <SectionTitle>Datos del taller</SectionTitle>
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
        <FormControl isRequired gridColumn={{ md: 'span 2' }}>
          <FormLabel fontSize="sm">Nombre</FormLabel>
          <Input value={f.nombre || ''} onChange={(e) => set('nombre', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Tipo</FormLabel>
          <Select value={f.tipo || 'Taller'} onChange={(e) => set('tipo', e.target.value)}>
            {TIPOS.map((v) => <option key={v} value={v}>{v}</option>)}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Fecha</FormLabel>
          <Input type="date" value={(f.fecha as string) || ''} onChange={(e) => set('fecha', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Horario</FormLabel>
          <Input placeholder="09:00 - 11:00" value={f.horario || ''} onChange={(e) => set('horario', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Sede</FormLabel>
          <Input value={f.sede || ''} onChange={(e) => set('sede', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Instructor</FormLabel>
          <Input value={f.instructor || ''} onChange={(e) => set('instructor', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Cupo</FormLabel>
          <Input type="number" min={0} value={f.cupo ?? ''} onChange={(e) => set('cupo', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Estatus</FormLabel>
          <Select value={f.estatus || 'Programado'} onChange={(e) => set('estatus', e.target.value)}>
            {ESTATUS_OPCIONES.map((v) => <option key={v} value={v}>{v}</option>)}
          </Select>
        </FormControl>
      </SimpleGrid>

      <SectionTitle>Detalle</SectionTitle>
      <SimpleGrid columns={{ base: 1 }} spacing={4}>
        <FormControl>
          <FormLabel fontSize="sm">Descripción</FormLabel>
          <Textarea rows={3} value={f.descripcion || ''} onChange={(e) => set('descripcion', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Observaciones</FormLabel>
          <Textarea rows={2} value={f.observaciones || ''} onChange={(e) => set('observaciones', e.target.value)} />
        </FormControl>
        <FormControl>
          <Checkbox isChecked={!!f.publicadoPortal} onChange={(e) => set('publicadoPortal', e.target.checked)} colorScheme="red">
            Publicar al portal ciudadano
          </Checkbox>
        </FormControl>
      </SimpleGrid>

      <HStack justify="flex-end" pt={2} spacing={3}>
        <Button variant="outline" onClick={onCancelar} isDisabled={guardando}>Cancelar</Button>
        <Button bg={IMM_COLOR} color="white" _hover={{ bg: IMM_COLOR_HOVER }} onClick={handleGuardar} isLoading={guardando} loadingText="Guardando...">
          Guardar taller
        </Button>
      </HStack>
    </VStack>
  );
};

export default FormularioTaller;
